import React, { PropsWithChildren } from 'react';
import { Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { getStripeLocale } from '@front/common/src/utils/stripe';
import { getCurrentCurrency } from '@front/common/src/utils/country';
import { toRem } from '@front/common/src/utils/styles';
import colors from '@front/common/src/utils/colors';

const PaymentProvider = ({
  children,
  stripePromise,
}: PropsWithChildren<{ stripePromise: Promise<Stripe | null> | null }>) => (
  <Elements
    stripe={stripePromise}
    options={{
      locale: getStripeLocale(),
      // 'mode' and 'currency' should be defined otherwise it crashes
      // 'mode' will be updated in payment-section component with the correct value
      // Currency will be optional in stripe-js 8.1.0
      // @see https://github.com/stripe/stripe-js/releases/tag/v8.1.0
      mode: 'setup',
      currency: getCurrentCurrency().toLowerCase(),
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800;900&display=swap',
        },
      ],
      appearance: {
        theme: 'flat',
        variables: {
          fontFamily: "'Work Sans', sans-serif",
          fontSizeXs: toRem(16),
          fontSizeSm: toRem(13),
          borderRadius: '8px',
          colorPrimary: colors.ink100,
          colorTextSecondary: colors.ink100,
          colorText: colors.ink100,
          colorDanger: colors.error100,
        },
        rules: {
          '.AccordionItem': {
            padding: '17px',
          },
          '.AccordionItem--selected': {
            borderColor: colors.primary100,
            borderWidth: '3px',
            borderStyle: 'solid',
          },
          '.PickerItem': {
            backgroundColor: colors.white100,
            borderColor: colors.ink20,
            borderWidth: '1px',
            borderStyle: 'solid',
          },
          '.PickerItem--highlight': {
            borderColor: colors.primary100,
            borderWidth: '3px',
            boxShadow: 'none',
            borderStyle: 'solid',
          },
          '.PickerItem--highlight:hover': {
            boxShadow: 'none',
          },
          '.Input': {
            backgroundColor: colors.ink10,
          },
          '.Input:focus': {
            backgroundColor: colors.white100,
            boxShadow: 'none',
            borderWidth: '3px',
            borderStyle: 'solid',
            borderColor: colors.primary100,
          },
        },
      },
    }}
  >
    {children}
  </Elements>
);

export default PaymentProvider;
